import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

export interface PersonalMute {
  id: number;
  muterId: string;
  mutedId: string;
  createdAt: string;
  username: string | null;
  avatarImageUrl: string | null;
}

export function usePersonalMutes() {
  const queryClient = useQueryClient();

  // Fetch the user's personal mute list
  const {
    data: mutedUsers = [],
    isLoading: mutesLoading,
    error: mutesError,
    refetch: refetchMutes,
  } = useQuery<PersonalMute[]>({
    queryKey: ['/api/user/muted-users'],
    select: (data: any) => data?.mutedUsers || [],
  });

  // Fetch just the muted user IDs for filtering
  const { data: mutedUserIds = [] } = useQuery<string[]>({
    queryKey: ['/api/user/muted-user-ids'],
    select: (data: any) => data?.mutedUserIds || [],
  });

  // Mute a user
  const muteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiRequest('POST', `/api/user/muted-users/${userId}`);
    },
    onSuccess: () => {
      // Invalidate both queries to refresh the mute list
      queryClient.invalidateQueries({ queryKey: ['/api/user/muted-users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/muted-user-ids'] });
    },
  });

  // Unmute a user
  const unmuteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiRequest('DELETE', `/api/user/muted-users/${userId}`);
    },
    onSuccess: () => {
      // Invalidate both queries to refresh the mute list
      queryClient.invalidateQueries({ queryKey: ['/api/user/muted-users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/muted-user-ids'] });
    },
  });

  // Check if a specific user is muted
  const isUserMuted = (userId: string) => {
    return mutedUserIds.includes(userId);
  };

  return {
    mutedUsers,
    mutedUserIds,
    mutesLoading,
    mutesError,
    refetchMutes,
    muteUser: muteUserMutation.mutateAsync,
    isMuting: muteUserMutation.isPending,
    unmuteUser: unmuteUserMutation.mutateAsync,
    isUnmuting: unmuteUserMutation.isPending,
    isUserMuted,
  };
}
